/**
 * Terminology Server RTK Query API.
 * Uses token vending from our backend (/api/terminology/token) and forwards to /ts-api.
 */
import {
    createApi,
    fetchBaseQuery,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { getOrFetchTerminologyToken, clearTerminologyToken } from '../api/terminologyToken';
import type {
    ChunkSearchResult,
    TermToCodeSearchParams,
    CodeMapParams,
    CodeMapResult,
    ConceptParentParams,
    ConceptParentResponse,
    ServiceStatus,
    ValueSetListItem,
    ValueSetDetailsResponse,
    ValueSetUploadStatusResponse,
    ValueSetShareResponse,
    TerminologyUser,
    DocumentSearchTaskStatusItem,
} from '../api/terminologyApi';

const TS_BASE = '/ts-api';

function removeEmptyValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (Array.isArray(value)) {
            if (value.length > 0) out[key] = value;
        } else if (value !== null && value !== undefined) {
            out[key] = value;
        }
    }
    return out as Partial<T>;
}

const terminologyBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    const token = await getOrFetchTerminologyToken();
    const isFormData = typeof args !== 'string' && args.body instanceof FormData;
    const baseQuery = fetchBaseQuery({
        baseUrl: TS_BASE,
        credentials: 'omit',
        prepareHeaders: headers => {
            if (!isFormData) {
                headers.set('Content-Type', 'application/json; charset=utf-8');
            }
            if (token) headers.set('Authorization', `Bearer ${token.trim()}`);
            return headers;
        },
    });

    let result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
        clearTerminologyToken();
        const newToken = await getOrFetchTerminologyToken();
        if (newToken) {
            result = await baseQuery(args, api, extraOptions);
        }
    }

    return result;
};

export const terminologyApi = createApi({
    reducerPath: 'terminologyApi',
    baseQuery: terminologyBaseQuery,
    tagTypes: ['Vocabs', 'ValueSets', 'ServiceStatus'],
    endpoints: builder => ({
        getVocabs: builder.query<string[], void>({
            query: () => '/api/get_vocabs',
            providesTags: ['Vocabs'],
        }),

        healthcheck: builder.query<{ status: string }, void>({
            query: () => '/api/healthcheck',
        }),

        termToCodeSearch: builder.mutation<Record<string, ChunkSearchResult>, TermToCodeSearchParams>({
            query: params => {
                const cleaned = removeEmptyValues({
                    ...params,
                    validity: params.validity ? true : null,
                    chunks: (params.chunks || []).filter(Boolean).map(c => String(c).trim()),
                });
                return { url: '/search', method: 'POST', body: cleaned };
            },
        }),

        contextResolutionSearch: builder.mutation<Record<string, ChunkSearchResult>, TermToCodeSearchParams>({
            query: params => {
                const cleaned = removeEmptyValues({
                    ...params,
                    validity: params.validity ? true : null,
                    chunks: (params.chunks || []).filter(Boolean).map(c => String(c).trim()),
                });
                return { url: '/api/search_by_context', method: 'POST', body: cleaned };
            },
        }),

        codeToCodeSearch: builder.mutation<{ message?: Record<string, unknown> }, CodeMapParams>({
            query: params => ({
                url: '/code_map',
                method: 'POST',
                body: removeEmptyValues({
                    codes: (params.codes || []).filter(Boolean).map(c => String(c).trim()),
                    source_vocabulary: params.source_vocabulary,
                    target_vocabulary: params.target_vocabulary,
                }),
            }),
        }),

        getConceptParent: builder.query<ConceptParentResponse, ConceptParentParams>({
            query: params => ({
                url: '/api/concept_parent',
                method: 'POST',
                body: params,
            }),
        }),

        submitDocumentSearchTask: builder.mutation<
            { task_id: string },
            { files: File[]; accurate?: boolean; entityConfig?: unknown }
        >({
            query: ({ files, accurate, entityConfig }) => {
                const formData = new FormData();
                files.forEach(f => formData.append('files', f));
                const endpoint = accurate ? '/api/accurate-document-search-task' : '/api/document-search-task';
                if (accurate && entityConfig) {
                    formData.append('is_accurate_search', 'true');
                    formData.append('entity_config', JSON.stringify({ entity_config: entityConfig }));
                }
                return { url: endpoint, method: 'POST', body: formData };
            },
        }),

        getDocumentSearchTaskStatus: builder.query<DocumentSearchTaskStatusItem[], string>({
            query: taskId => `/api/document-search-task/${taskId}`,
        }),

        getValueSets: builder.query<ValueSetListItem[], void>({
            query: () => '/api/valuesets',
            providesTags: ['ValueSets'],
        }),

        getValueSetDetails: builder.query<ValueSetDetailsResponse, string>({
            query: id => `/api/valuesets/details/${id}`,
            providesTags: (result, error, id) => [{ type: 'ValueSets', id }],
        }),

        deleteValueSet: builder.mutation<void, { id: string; type: string }>({
            query: ({ id, type }) => ({
                url: `/api/valuesets/delete?id=${encodeURIComponent(id)}&type=${encodeURIComponent(type)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ValueSets'],
        }),

        getServiceStatus: builder.query<ServiceStatus[], void>({
            query: () => '/service_status',
            providesTags: ['ServiceStatus'],
        }),

        uploadValueSets: builder.mutation<{ task_id: string }, { file: File; name?: string; id?: string }>({
            query: ({ file, name, id }) => {
                const formData = new FormData();
                formData.append('file', file);
                if (name) formData.append('name', name);
                if (id) formData.append('id', id);
                return { url: '/api/valuesets/upload', method: 'POST', body: formData };
            },
            invalidatesTags: ['ValueSets'],
        }),

        getValueSetUploadStatus: builder.query<ValueSetUploadStatusResponse, string>({
            query: taskId => `/api/valuesets/upload-status/${taskId}`,
        }),

        shareValueSet: builder.mutation<ValueSetShareResponse, string>({
            query: valuesetMetadataId => ({
                url: `/api/valuesets/share/${valuesetMetadataId}`,
                method: 'POST',
            }),
            invalidatesTags: ['ValueSets'],
        }),

        removeShareValueSet: builder.mutation<void, string>({
            query: valuesetMetadataId => ({
                url: `/api/valuesets/share/${encodeURIComponent(valuesetMetadataId)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ValueSets'],
        }),

        getSharedValueSet: builder.query<ValueSetShareResponse, string>({
            query: valuesetMetadataId => `/api/valuesets/shared/${valuesetMetadataId}`,
        }),

        getUsers: builder.query<TerminologyUser[], { search?: string; first?: number; max?: number }>({
            query: ({ search = '', first = 0, max = 5 }) =>
                `/api/user?search=${encodeURIComponent(search)}&first=${first}&max=${max}`,
        }),

        downloadValueSet: builder.query<Blob, { valueset_metadata_id?: string; type?: string }>({
            query: params => {
                const sp = new URLSearchParams();
                if (params.valueset_metadata_id) sp.set('valueset_metadata_id', params.valueset_metadata_id);
                if (params.type) sp.set('type', params.type);
                return {
                    url: `/api/valuesets/download?${sp.toString()}`,
                    responseHandler: res => res.blob(),
                };
            },
        }),
    }),
});

// Hooks used in Terminology Server feature (as of scan):
//   useGetVocabsQuery, useTermToCodeSearchMutation, useContextResolutionSearchMutation,
//   useLazyGetConceptParentQuery, useGetValueSetDetailsQuery, useGetServiceStatusQuery,
//   useGetValueSetsQuery, useDeleteValueSetMutation, useLazyDownloadValueSetQuery,
//   useGetSharedValueSetQuery, useLazyGetUsersQuery, useShareValueSetMutation,
//   useRemoveShareValueSetMutation, useCodeToCodeSearchMutation, useSubmitDocumentSearchTaskMutation,
//   useLazyGetDocumentSearchTaskStatusQuery, useUploadValueSetsMutation.
// Unused (exported for future use): useLazyGetVocabsQuery, useHealthcheckQuery,
//   useGetDocumentSearchTaskStatusQuery, useLazyGetValueSetsQuery, useLazyGetValueSetDetailsQuery,
//   useLazyGetServiceStatusQuery, useGetValueSetUploadStatusQuery, useLazyGetSharedValueSetQuery,
//   useGetUsersQuery.
export const {
    useGetVocabsQuery,
    useLazyGetVocabsQuery,
    useHealthcheckQuery,
    useTermToCodeSearchMutation,
    useContextResolutionSearchMutation,
    useCodeToCodeSearchMutation,
    useLazyGetConceptParentQuery,
    useSubmitDocumentSearchTaskMutation,
    useGetDocumentSearchTaskStatusQuery,
    useLazyGetDocumentSearchTaskStatusQuery,
    useGetValueSetsQuery,
    useLazyGetValueSetsQuery,
    useGetValueSetDetailsQuery,
    useLazyGetValueSetDetailsQuery,
    useDeleteValueSetMutation,
    useGetServiceStatusQuery,
    useLazyGetServiceStatusQuery,
    useUploadValueSetsMutation,
    useGetValueSetUploadStatusQuery,
    useShareValueSetMutation,
    useRemoveShareValueSetMutation,
    useGetSharedValueSetQuery,
    useLazyGetSharedValueSetQuery,
    useGetUsersQuery,
    useLazyGetUsersQuery,
    useLazyDownloadValueSetQuery,
} = terminologyApi;

/** Transform code_map API response to CodeMapResult[] (same as old terminologyApi). */
export function parseCodeMapResults(
    raw: { message?: Record<string, unknown> },
    sourceVocabulary: string
): CodeMapResult[] {
    const results: CodeMapResult[] = [];
    let id = 0;
    const message = raw.message;
    if (!message || typeof message !== 'object') return results;
    for (const entries of Object.values(message)) {
        if (!Array.isArray(entries)) continue;
        for (const entry of entries) {
            if (!entry || typeof entry !== 'object') continue;
            const mappings = (entry as { mappings?: Record<string, string[]> }).mappings;
            if (!mappings || typeof mappings !== 'object') continue;
            const vocabulary = (entry as { vocabulary?: string }).vocabulary ?? sourceVocabulary;
            const code = (entry as { code?: string }).code ?? '';
            for (const [targetMap, targetCodes] of Object.entries(mappings)) {
                if (!Array.isArray(targetCodes)) continue;
                id++;
                results.push({
                    id,
                    source_vocab: vocabulary,
                    source_code: code,
                    target_vocab: targetMap,
                    target_codes: targetCodes,
                });
            }
        }
    }
    return results;
}
